import { action, computed } from 'mobx'
import type React from 'react'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Cubit } from '../core'
import { ObservableMapStore } from '../../stores/ObservableMapStore'

export type FormFieldError<T> = {
  type: 'required' | 'min' | 'max' | 'invalid'
  i18nKey: T
  args?: Record<string, unknown>
}

export class FormCubit<
  Fields extends object,
  ErrorList extends string,
  Errors extends Record<keyof Fields, FormFieldError<ErrorList>> = Record<
    keyof Fields,
    FormFieldError<ErrorList>
  >,
> extends Cubit {
  repository: FirebaseRepository
  fields: ObservableMapStore<Fields>
  errors: ObservableMapStore<Errors>

  constructor(
    repository: FirebaseRepository,
    options: {
      initialFields?: Fields
    } = {}
  ) {
    super()
    this.repository = repository
    this.fields = new ObservableMapStore<Fields>(options.initialFields)
    this.errors = new ObservableMapStore<Errors>()
  }

  field<K extends keyof Fields>(
    name: K,
    callback: (field: {
      value: Fields[K] | undefined
      setValue: (value: Fields[K]) => void
      error?: FormFieldError<ErrorList>
    }) => React.ReactNode
  ) {
    const value = this.fields.get(name)
    const error = this.errors.get(name)

    return callback({
      value: value,
      setValue: (value) => this.fields.set(name, value),
      error,
    })
  }

  @action
  setField(name: keyof Fields, value: Fields[keyof Fields]) {
    this.fields.set(name, value)
  }

  @computed
  get isValid() {
    return this.errors.size === 0
  }

  @action
  reset() {
    this.fields.clear()
    this.errors.clear()
  }
}
